import React from 'react';
import { Card, CardHeader, CardContent, Box, Typography, IconButton, Avatar, ButtonBase, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MailIcon from '@mui/icons-material/Mail';
import StarIcon from '@mui/icons-material/Star';
import { useTranslation } from '../../../hooks/useTranslation';

export const WidgetFavorites = ({ onRemove, isEditMode }) => {
    const t = useTranslation('Dashboard.json');
    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const favoriteApps = [
        { name: t('widget.favorites.app.dashboard'), icon: <DashboardIcon />, color: '#667eea' },
        { name: t('widget.favorites.app.orders'), icon: <ShoppingCartIcon />, color: '#4CAF50' },
        { name: t('widget.favorites.app.clients'), icon: <PeopleIcon />, color: '#2196F3' },
        { name: t('widget.favorites.app.stats'), icon: <AssessmentIcon />, color: '#9C27B0' },
        { name: t('widget.favorites.app.messages'), icon: <MailIcon />, color: '#FF9800' },
        { name: t('widget.favorites.app.settings'), icon: <SettingsIcon />, color: '#607D8B' },
    ];

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                bgcolor: 'background.paper',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light'
                }
            }}
        >
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#FFD93D', width: 32, height: 32 }}>
                            <StarIcon sx={{ fontSize: 18, color: '#fff' }} />
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'Poppins' }}>
                                {t('widget.favorites.title')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('widget.favorites.subtitle')}
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    isEditMode && (
                        <IconButton
                            className="widget-remove-btn"
                            onClick={handleRemove}
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                opacity: 0,
                                pointerEvents: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.main'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )
                }
                sx={{
                    cursor: 'move',
                    pb: 1,
                    bgcolor: 'transparent',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            />
            <CardContent sx={{ flex: 1, pt: 0 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1.5,
                        height: '100%'
                    }}
                >
                    {favoriteApps.map((app, index) => (
                        <ButtonBase
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.25,
                                p: 2,
                                borderRadius: '12px',
                                transition: 'all 0.2s ease',
                                border: '1px solid transparent',
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.02)',
                                    transform: 'translateY(-2px)',
                                    borderColor: app.color,
                                    boxShadow: `0 4px 12px ${app.color}20`
                                }
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 52,
                                    height: 52,
                                    bgcolor: `${app.color}15`,
                                    color: app.color,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: app.color,
                                        color: 'white'
                                    }
                                }}
                            >
                                {React.cloneElement(app.icon, { sx: { fontSize: 26 } })}
                            </Avatar>
                            <Typography
                                sx={{
                                    fontSize: 'clamp(11px, 1.1vw, 13px)',
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    fontFamily: 'Poppins',
                                    textAlign: 'center'
                                }}
                            >
                                {app.name}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};
